import { takeEvery, put, all, call } from 'redux-saga/effects';
import i18next from 'commons/src/i18n';
import history from 'commons/src/store/history';
import { getUserPreferencesSuccess, INITIALIZE, initializeSuccess } from '../actions';
import {
    fetchPublicDeviceData,
    fetchPublicDeviceSensorData,
    FetchPublicDeviceDataType,
    fetchPublicDeviceDataError,
} from '../actions/actions';
import { getPublicDashboard } from '../actions/publicDashboardActions';
import { publicDashboardUrl } from '../constants';
import fetchPublicRadonDataSaga from './radonSaga';

const isIEBrowser = () => !!navigator.userAgent.match(/MSIE/g) || !!navigator.userAgent.match(/Trident/g);

const hasNoLocalStorage = () => {
    try {
        const test = 'test';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return false;
    } catch (e) {
        return true;
    }
};

const getUrlParams = (): {
    lang: string | null;
    color: string | null;
    geoHash: string | null;
    measurementSystem: string | null;
    numberOfTilesDefault: string | null;
} => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    const color = params.get('color');
    const geoHash = params.get('geohash');
    const measurementSystem = params.get('unit');
    const numberOfTilesDefault = params.get('tiles-in-width');
    return { lang, color, geoHash, measurementSystem, numberOfTilesDefault };
};

export function* initializeAppSaga(): Generator {
    try {
        const { lang, color: backgroundColor, measurementSystem, numberOfTilesDefault } = getUrlParams();
        const userLanguage = lang || navigator.languages[0] || navigator.language;

        if (localStorage.getItem('i18nextLng') !== userLanguage) {
            yield i18next.changeLanguage(userLanguage);
        }

        if (isIEBrowser() || hasNoLocalStorage()) {
            // check, if browser is IE
            history.push('/unsupported');
        } else {
            const pathname = window.location.pathname;
            const deviceKey = pathname.substring(pathname.lastIndexOf('/') + 1);
            const meetingBackground = pathname.includes('meeting-background');
            const publicDashboard = pathname.includes(publicDashboardUrl);
            const requiredMeasurementSystem = (measurementSystem && measurementSystem.toUpperCase()) || 'METRIC';
            const payload: FetchPublicDeviceDataType = {
                measurementSystem: requiredMeasurementSystem,
                publicUrlPath: deviceKey,
                showAllSensors: meetingBackground,
                backgroundColor: backgroundColor || undefined,
            };
            yield put(
                getUserPreferencesSuccess({
                    measurementSystem: requiredMeasurementSystem,
                    tilesInWidth: numberOfTilesDefault,
                })
            );
            if (publicDashboard && deviceKey && deviceKey !== publicDashboardUrl) {
                yield put(
                    getPublicDashboard({ measurementSystem: requiredMeasurementSystem, dashboardUrl: deviceKey })
                );
            } else {
                const geoHash = getUrlParams().geoHash;
                if (meetingBackground && geoHash) {
                    yield call(fetchPublicRadonDataSaga, { geoHash });
                }
                yield all([put(fetchPublicDeviceData(payload)), put(fetchPublicDeviceSensorData(payload))]);
            }
            yield put(initializeSuccess());
        }
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        yield put(fetchPublicDeviceDataError(error.message));
    }
}

export default function* () {
    yield all([takeEvery(INITIALIZE, initializeAppSaga)]);
}
